import React from 'react';
import styled from 'styled-components';
import { breakpoints } from '@constants/breakpoints';
import { Body5 } from '@ui';
import { TransparentFlatButton } from '@ui/buttons';
import { useHoverState } from '@hooks/application/useHoverState';
import { extractTextFromChildren } from '../utils/extractText';
import { MoreDetailDepthContext } from '../contexts/MoreDetailDepthContext';
import { InfoIcon } from '@assets/icons/svgs/InfoIcon';

// --- Styled Components ---

const Wrapper = styled.li`
  position: relative;
  margin-bottom: 0;
  padding-bottom: 8px;
`;

const BulletContent = styled.div`
  position: relative;
  margin-bottom: 24px;

  .more-detail-result {
    border-radius: 5px;
    cursor: text;
    transition: transform 0.3s ease;
    background: ${({ theme }) => theme.themeColors.componentNoOpacity};

    p,
    strong {
      margin: 0;
    }

    * {
      font-size: 13px !important;
      line-height: 16px !important;
      letter-spacing: -0.03em !important;
    }
  }

  @media (max-width: ${() => breakpoints.mobile}) {
    margin-right: 36px;
  }

  z-index: 3;
`;

const IconPositioner = styled.div`
  position: absolute;
  top: 0;
  right: -12px;
  bottom: 0;
  width: 30px;
  z-index: 5;

  @media (max-width: ${() => breakpoints.mobile}) {
    right: -44px;
  }
`;

const IconBackground = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;

  padding: 4px;
  background: ${({ theme }) => theme.themeColors.primaryCtaButton};
  cursor: pointer;
  margin-right: 16px;

  border-radius: 2px;
  z-index: 8;

  display: flex;
  flex-direction: row;
  align-items: center;

  svg {
    z-index: 5;
    height: 15px;
    width: 15px;

    * {
      fill: ${({ theme }) => theme.themeColors.buttonText};
    }
  }
`;

const ResultArea = styled.div`
  padding: 16px;

  p {
    padding-bottom: 16px;
  }
`;

const ToggleButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  padding: 0 16px;

  button {
    padding: 4px 8px 0 8px;
  }
`;

const MoreDetailIcon = ({ onClick }) => (
  <IconPositioner>
    <IconBackground onClick={onClick}>
      <InfoIcon />
    </IconBackground>
  </IconPositioner>
);

const MoreDetailResult = ({ visible, onToggle }) => {
  if (!visible) {
    return (
      <ToggleButtonRow>
        <TransparentFlatButton onClick={onToggle}>Show More Detail</TransparentFlatButton>
      </ToggleButtonRow>
    );
  }

  return (
    <MoreDetailDepthContext.Provider value={true}>
      <ResultArea className="more-detail-result">
        <ToggleButtonRow>
          <TransparentFlatButton onClick={onToggle}>Hide More Detail</TransparentFlatButton>
        </ToggleButtonRow>
        {/* Placeholder: streaming result will be rendered here */}
      </ResultArea>
    </MoreDetailDepthContext.Provider>
  );
};

const onGetMoreDetail = (text) => {
  console.log('onGetMoreDetail', text);
};

export const MoreDetail = ({ children }) => {
  const isNested = React.useContext(MoreDetailDepthContext);
  const { isHovering, onMouseEnter, onMouseLeave } = useHoverState(600);

  const [isActivated, setIsActivated] = React.useState(false);
  const [resultVisible, setResultVisible] = React.useState(true);

  if (isNested) {
    return (
      <li>
        <Body5>{children}</Body5>
      </li>
    );
  }

  const handleIconClick = () => {
    const text = extractTextFromChildren(children);
    onGetMoreDetail(text);
    setIsActivated(true);
  };

  const showIcon = !isActivated && isHovering;

  return (
    <Wrapper onMouseOver={onMouseEnter} onMouseOut={onMouseLeave}>
      <BulletContent>
        <Body5>{children}</Body5>
        {showIcon && <MoreDetailIcon onClick={handleIconClick} />}
      </BulletContent>

      {isActivated && <MoreDetailResult visible={resultVisible} onToggle={() => setResultVisible((v) => !v)} />}
    </Wrapper>
  );
};
